/**
 * A random-enough idempotency key for a payment request — safe outside a secure context.
 *
 * `crypto.randomUUID()` is only defined in a *secure context* (HTTPS, or `localhost`).
 * `docker-compose.node.yml` serves the kiosk over plain HTTP on the LAN, so a terminal opened
 * by the Pi's LAN address (the normal way a real venue reaches it) has no `randomUUID` at
 * all — calling it throws `TypeError: crypto.randomUUID is not a function`. Dev doesn't catch
 * this because `localhost` counts as secure.
 *
 * `crypto.getRandomValues()` has no such restriction and is what `randomUUID()` builds on
 * internally, so a v4 UUID assembled from it is exactly as random. Callers should still
 * generate the key *inside* whatever `try` guards the request that uses it — the point of
 * this helper is that it won't throw, not that nothing else can.
 */
export function randomIdempotencyKey() {
  if (typeof crypto?.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to getRandomValues — some embedded WebViews expose the function
      // but still throw when the context isn't secure.
    }
  }
  if (typeof crypto?.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join(''),
    ].join('-');
  }
  // Last resort — not cryptographically strong, but an idempotency key only needs to be
  // unique per attempt on this device, not unguessable. `crypto` missing entirely would mean
  // no Web Crypto API at all, which no browser the kiosk targets actually ships without.
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
